import * as React from 'react'
import { createCheckoutEvent } from '@/scopes/checkout/domain/analytics/index'
import { CheckoutEventProps } from '@/scopes/checkout/domain/analytics/types'
import { useCheckoutContext } from '@/scopes/checkout/domain/CheckoutContext'
import { useBookingDraftQuery, useCheckoutSid } from '@/scopes/checkout/domain/hooks'
import { useSendOptimizelyEvent } from '@/scopes/common/infrastructure/ab-testing/optimizely/useSendOptimizelyEvent'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createNewRelicLogger } from '@/scopes/common/infrastructure/logging/newRelicLogger'

function createErrorDescription(errorMessage: string | null): string {
  return errorMessage || 'Failed to load booking details'
}

export function useSendCheckoutEvent(optimizelyEventName?: string) {
  const { sendEvent } = useAnalytics()
  const sid = useCheckoutSid()
  const { isLoading, data } = useBookingDraftQuery()
  const { sendEvent: sendOptimizelyEvent } = useSendOptimizelyEvent()
  const { step } = useCheckoutContext()
  const logger = React.useMemo(() => createNewRelicLogger({ scope: 'checkout' }), [])

  const hasBeenSent = React.useRef(false)

  const sendCheckoutEvent = React.useCallback(() => {
    if (!data)
      return

    const hasDraftError = Boolean(data.error?.hasError)
    const resolvedStep = data?.analytics?.step ?? step

    const event = createCheckoutEvent({
      userId: data?.draftId ?? '',
      ...data?.analytics,
      step: resolvedStep,
      ...(hasDraftError && { errorDescription: createErrorDescription(data.error?.message ?? null) }),
    } as CheckoutEventProps)

    sendEvent(event)
    logger.setContext('draftId', data.draftId ?? 'unknown')
    logger.logEvent(hasDraftError ? 'checkout_datalayer_push_with_error' : 'checkout_datalayer_push', {
      ...event,
      ecommerce: JSON.stringify(event.ecommerce),
      checkoutStep: event.ecommerce.checkout.actionField.step ?? step,
      ...(hasDraftError ? { errorMessage: data.error?.message } : {}),
    })
  }, [sendEvent, data, logger, step])

  React.useEffect(() => {
    if (!sid)
      return
    if (isLoading)
      return
    if (hasBeenSent.current)
      return

    sendCheckoutEvent()
    if (optimizelyEventName)
      sendOptimizelyEvent(optimizelyEventName)
    hasBeenSent.current = true
  }, [sid, isLoading, sendCheckoutEvent, data, sendOptimizelyEvent, optimizelyEventName, hasBeenSent])

  return {
    sendCheckoutEvent,
  }
}
